import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/__next__';
import Dialog from 'carbon-react/lib/components/dialog';
import Loader from 'carbon-react/lib/components/loader/__next__';
import Message from 'carbon-react/lib/components/message';
import { type FormEventHandler, useState } from 'react';
import { useProvisionTestJob } from '../../../helpers/use-provision-test-job';
import MissingPermissions from '../../MissingPermissions/MissingPermissions';

interface CheckCompanyPermissionsProps {
  intacctCompanyId: string;
  companyTreeSettingsId: number;
}

export function CheckCompanyPermissions({
  intacctCompanyId,
  companyTreeSettingsId,
}: CheckCompanyPermissionsProps) {
  const {
    run: checkPermissions,
    isPending,
    result: data,
    error,
  } = useProvisionTestJob();
  const [isRightsOpen, setIsRightsOpen] = useState(false);
  const [isRawJsonOpen, setIsRawJsonOpen] = useState(false);

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    if (isPending) return;
    checkPermissions({
      companyId: intacctCompanyId,
    });
  };

  const handleOpenRights = () => setIsRightsOpen(true);
  const handleCloseRights = () => setIsRightsOpen(false);
  const handleOpenRawJson = () => setIsRawJsonOpen(true);
  const handleCloseRawJson = () => setIsRawJsonOpen(false);

  const userRights = data?.userRights ?? [];
  const hasUserRights = userRights.length > 0;
  const groupedUserRights = Object.entries(
    userRights.reduce<Record<string, typeof userRights>>((acc, right) => {
      if (!acc[right.application]) {
        acc[right.application] = [];
      }
      acc[right.application]!.push(right);
      return acc;
    }, {}),
  )
    .sort(([appA], [appB]) => appA.localeCompare(appB))
    .map(
      ([application, rights]) =>
        [
          application,
          rights.sort((a, b) => a.policy.localeCompare(b.policy)),
        ] as const,
    );

  return (
    <Box display="flex" flexDirection="column" gap={2} alignItems="end">
      <Button onClick={handleSubmit}>
        {isPending ? (
          <Loader
            loaderType="ring"
            variant="inline"
            loaderLabel="Checking..."
            size="small"
          />
        ) : (
          'Check'
        )}
      </Button>
      {error ? <Message variant="error">{error.message}</Message> : null}
      {data?.missingPermissions ? (
        data.missingPermissions.length > 0 ? (
          <MissingPermissions
            missing={data.missingPermissions}
            companyTreeSettingsId={companyTreeSettingsId}
          />
        ) : (
          <div>The WSU has all required permissions</div>
        )
      ) : null}
      {data ? (
        <Box display="flex" gap={2}>
          <Button variantType="secondary" onClick={handleOpenRawJson}>
            View raw JSON
          </Button>
          <Button onClick={handleOpenRights}>View user rights</Button>
          <Dialog
            onCancel={handleCloseRights}
            title="User rights"
            open={isRightsOpen}
          >
            {!hasUserRights ? (
              <div>No user rights returned for this company.</div>
            ) : (
              <div>
                {groupedUserRights.map(([application, rights]) => (
                  <div key={application}>
                    <strong>{application}</strong>
                    <ul>
                      {rights.map((right) => (
                        <li key={`${right.application}-${right.policy}`}>
                          <strong>{right.policy}</strong>:{' '}
                          {right.rights.join(', ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <div className="intacct-admin-modal-footer">
              <Button onClick={handleCloseRights}>Close</Button>
            </div>
          </Dialog>
          <Dialog
            onCancel={handleCloseRawJson}
            title="Provision test response"
            open={isRawJsonOpen}
            size="large"
          >
            <pre>{JSON.stringify(data, null, 2)}</pre>
            <div className="intacct-admin-modal-footer">
              <Button onClick={handleCloseRawJson}>Close</Button>
            </div>
          </Dialog>
        </Box>
      ) : null}
    </Box>
  );
}
